import type { BulkApplyRunOutcome } from "../../../context/BulkApplyRunContext";
import type { DroppedCard } from "../../../utils/redline-bulk-apply-local";
import type { PlaceOutcome } from "./change-throughout-selections";

export interface ChangeThroughoutPlaceRule {
  ruleId: string;
  matchId: string;
}

export type PlaceRulesByCardId = ReadonlyMap<string, readonly ChangeThroughoutPlaceRule[]>;

export interface ChangeThroughoutRunOutcome {
  cardOutcome: BulkApplyRunOutcome;
  placeOutcomesByCardId: Map<string, Map<string, PlaceOutcome>>;
}

type CardRollUp =
  | { result: "applied" }
  | { result: "partlyApplied"; reason: string }
  | { result: "notChosen"; reason: string; conflictingRuleIds: string[] }
  | { result: "notApplied"; reason: string };

export const PLACE_NOT_APPLIED_REASON = "This place could not be changed during Apply All.";
export const PLACE_NOT_CHOSEN_REASON = "Another change kept these words.";
export const NO_PLACE_COULD_BE_CHANGED_REASON = "No place on this card can be changed.";

function placesAppliedReasonOf(appliedCount: number, placeCount: number): string {
  return `${appliedCount} of ${placeCount} places applied. Each place says how it went.`;
}

function placeOutcomeOf(ruleId: string, outcome: BulkApplyRunOutcome): PlaceOutcome {
  if (outcome.notChosenRuleIds.includes(ruleId)) {
    const notChosenCard = outcome.notChosenCards.find((card) => card.ruleId === ruleId);
    return { result: "notChosen", reason: notChosenCard?.reason ?? PLACE_NOT_CHOSEN_REASON };
  }
  const partlyAppliedCard = outcome.partiallyAppliedCards.find((card) => card.ruleId === ruleId);
  if (partlyAppliedCard !== undefined && !partlyAppliedCard.everyWriteLanded) return { result: "notApplied", reason: partlyAppliedCard.reason };
  if (outcome.creditedRuleIds.includes(ruleId)) return { result: "applied" };
  const droppedCard = outcome.droppedCards.find((card) => card.ruleId === ruleId);
  if (droppedCard !== undefined) return { result: "notApplied", reason: droppedCard.reason };
  const failedRule = outcome.failedRules.find((rule) => rule.ruleId === ruleId);
  return { result: "notApplied", reason: failedRule?.reason ?? PLACE_NOT_APPLIED_REASON };
}

export interface PlaceNotApplied {
  ruleId: string;
  outcome: "not_chosen" | "not_applied";
}

const PLACE_OUTCOMES_FOR_AGENT = { notApplied: "not_applied", notChosen: "not_chosen" } as const;

export function placesNotAppliedOf(placeRuleIds: readonly string[], outcome: BulkApplyRunOutcome): PlaceNotApplied[] {
  return placeRuleIds.flatMap((ruleId) => {
    const placeOutcome = placeOutcomeOf(ruleId, outcome);
    if (placeOutcome.result === "applied") return [];
    return [{ ruleId, outcome: PLACE_OUTCOMES_FOR_AGENT[placeOutcome.result] }];
  });
}

function cardIdOfRuleIdIn(placeRulesByCardId: PlaceRulesByCardId): ReadonlyMap<string, string> {
  return new Map([...placeRulesByCardId].flatMap(([cardId, placeRules]) => placeRules.map((placeRule) => [placeRule.ruleId, cardId] as const)));
}

function conflictingCardIdsOf(placeRules: readonly ChangeThroughoutPlaceRule[], outcome: BulkApplyRunOutcome, cardIdOfRuleId: ReadonlyMap<string, string>): string[] {
  const placeRuleIds = new Set(placeRules.map((placeRule) => placeRule.ruleId));
  const conflictingRuleIds = outcome.notChosenCards.filter((card) => placeRuleIds.has(card.ruleId)).flatMap((card) => card.conflictingRuleIds);
  return [...new Set(conflictingRuleIds.map((ruleId) => cardIdOfRuleId.get(ruleId) ?? ruleId))];
}

function rollUpOf(placeRules: readonly ChangeThroughoutPlaceRule[], placeOutcomes: PlaceOutcome[], outcome: BulkApplyRunOutcome, cardIdOfRuleId: ReadonlyMap<string, string>): CardRollUp {
  if (placeOutcomes.length === 0) return { result: "notApplied", reason: NO_PLACE_COULD_BE_CHANGED_REASON };
  const appliedCount = placeOutcomes.filter((placeOutcome) => placeOutcome.result === "applied").length;
  if (appliedCount === placeOutcomes.length) return { result: "applied" };
  if (appliedCount > 0) return { result: "partlyApplied", reason: placesAppliedReasonOf(appliedCount, placeOutcomes.length) };
  const firstNotApplied = placeOutcomes.find((placeOutcome) => placeOutcome.result === "notApplied");
  if (firstNotApplied !== undefined) return { result: "notApplied", reason: firstNotApplied.reason };
  return { result: "notChosen", reason: PLACE_NOT_CHOSEN_REASON, conflictingRuleIds: conflictingCardIdsOf(placeRules, outcome, cardIdOfRuleId) };
}

function withoutPlaceRules(outcome: BulkApplyRunOutcome, placeRuleIds: ReadonlySet<string>): BulkApplyRunOutcome {
  const keptRuleIds = (ruleIds: string[]): string[] => ruleIds.filter((ruleId) => !placeRuleIds.has(ruleId));
  const keptCards = <Card extends DroppedCard>(cards: Card[]): Card[] => cards.filter((card) => !placeRuleIds.has(card.ruleId));
  return {
    ...outcome,
    creditedRuleIds: keptRuleIds(outcome.creditedRuleIds),
    failedRules: outcome.failedRules.filter((rule) => !placeRuleIds.has(rule.ruleId)),
    droppedCards: keptCards(outcome.droppedCards),
    partiallyAppliedRuleIds: keptRuleIds(outcome.partiallyAppliedRuleIds),
    partiallyAppliedCards: keptCards(outcome.partiallyAppliedCards),
    redlineAppliedRuleIds: keptRuleIds(outcome.redlineAppliedRuleIds),
    notChosenRuleIds: keptRuleIds(outcome.notChosenRuleIds),
    notChosenCards: keptCards(outcome.notChosenCards),
    partlySupersededCards: keptCards(outcome.partlySupersededCards),
  };
}

function addCardRollUp(cardOutcome: BulkApplyRunOutcome, cardId: string, cardRollUp: CardRollUp): void {
  switch (cardRollUp.result) {
    case "applied":
      cardOutcome.creditedRuleIds.push(cardId);
      cardOutcome.redlineAppliedRuleIds.push(cardId);
      return;
    case "partlyApplied":
      cardOutcome.creditedRuleIds.push(cardId);
      cardOutcome.partiallyAppliedRuleIds.push(cardId);
      cardOutcome.partiallyAppliedCards.push({ ruleId: cardId, reason: cardRollUp.reason, conflictingRuleIds: [], everyWriteLanded: false });
      return;
    case "notChosen":
      cardOutcome.notChosenRuleIds.push(cardId);
      cardOutcome.notChosenCards.push({ ruleId: cardId, reason: cardRollUp.reason, conflictingRuleIds: cardRollUp.conflictingRuleIds });
      return;
    case "notApplied":
      cardOutcome.failedRules.push({ ruleId: cardId, reason: cardRollUp.reason });
      cardOutcome.droppedCards.push({ ruleId: cardId, reason: cardRollUp.reason, conflictingRuleIds: [] });
      return;
  }
}

export function changeThroughoutRunOutcomeOf(outcome: BulkApplyRunOutcome, placeRulesByCardId: PlaceRulesByCardId): ChangeThroughoutRunOutcome {
  const cardIdOfRuleId = cardIdOfRuleIdIn(placeRulesByCardId);
  const cardOutcome = withoutPlaceRules(outcome, new Set(cardIdOfRuleId.keys()));
  const placeOutcomesByCardId = new Map<string, Map<string, PlaceOutcome>>();
  for (const [cardId, placeRules] of placeRulesByCardId) {
    const placeOutcomes = new Map(placeRules.map((placeRule) => [placeRule.matchId, placeOutcomeOf(placeRule.ruleId, outcome)] as const));
    placeOutcomesByCardId.set(cardId, placeOutcomes);
    addCardRollUp(cardOutcome, cardId, rollUpOf(placeRules, [...placeOutcomes.values()], outcome, cardIdOfRuleId));
  }
  return { cardOutcome, placeOutcomesByCardId };
}

export function cardIdOfRunRule(placeRulesByCardId: PlaceRulesByCardId, ruleId: string): string {
  return cardIdOfRuleIdIn(placeRulesByCardId).get(ruleId) ?? ruleId;
}
